import { gql } from '@apollo/client/core';

export const SETTLEMENT_ATTACHMENT_FIELDS = gql`
  fragment SettlementAttachmentFields on SettlementAttachment {
    id
    identifier
    url
  }
`;

/**
 * The legacy query selected `id` too; its mapper was what dropped it. The tax write needs it: it is
 * echoed back into `address_attributes`, so Rails updates the existing row instead of building a
 * new `Address` on every save.
 */
export const SETTLEMENT_ADDRESS_FIELDS = gql`
  fragment SettlementAddressFields on Address {
    id
    city
    country
    postalCode
    state
    street1
    street2
  }
`;

export const SETTLEMENT_EVENT_FIELDS = gql`
  fragment SettlementEventFields on SettlementEvent {
    id
    startTime
    doorTime
    endTime
    timezone
  }
`;

/**
 * Scalars only. AppSchema also publishes `deal { key name comment calculation }` and `attachments`
 * on a deal row, and the legacy query selected both, but nothing reads either -- `SettlementDeal.vue`
 * renders `dealType` through its own label map. Leaving them out also keeps the row's shape exactly
 * `Settlement.SettlementDeal`, which has no field for either.
 */
export const SETTLEMENT_DEAL_FIELDS = gql`
  fragment SettlementDealFields on SettlementDeal {
    id
    dealType
    amount
    percent
    expenseAmount
    firstAmount
    hitAmount
    showTicketSales
  }
`;

export const SETTLEMENT_CUSTOM_EXPENSE_FIELDS = gql`
  fragment SettlementCustomExpenseFields on SettlementCustomExpense {
    id
    amount
    description
    due
    deductedType
  }
`;

export const SETTLEMENT_MANUAL_TICKET_SALE_FIELDS = gql`
  fragment SettlementManualTicketSaleFields on SettlementManualTicketSale {
    id
    description
    price
    ticketsSold
  }
`;

export const SETTLEMENT_PAYMENT_FIELDS = gql`
  fragment SettlementPaymentFields on SettlementPayment {
    id
    description
    amount
    checkWireNr
    dateDue
    datePaid
    paymentType
    attachments {
      ...SettlementAttachmentFields
    }
  }
  ${SETTLEMENT_ATTACHMENT_FIELDS}
`;

/**
 * The legacy `eventArtistSettlement` selection, minus what nothing reads, plus the address `id`.
 * The exclusions are the complete list:
 *
 * - `deals` -- only `barDeals` and `ticketDeals` are consumed.
 * - `signedContracts`, `createdAt`/`updatedAt` -- declared on the admin store's old local interface,
 *   which this replaces, and read by nothing.
 * - `payee` on a payment row, and `deal { ... }` / `attachments` on a deal row.
 * - `_destroy` on the row collections. Input-only, and already applied by the time it answers.
 * - `taxAttachments`. Unread, and there is a second reason:
 *   `App::Settlement::SettlementAttachmentType#id` resolves nil for uploader-backed rows, which is
 *   exactly what `tax_attachments` is, so every row would be discarded by a mapper whose domain
 *   shape requires an id. Payment attachments are a different case -- those come through a struct
 *   that carries one.
 * - `date` on the event rows. Unread, and no longer the same value the legacy field returned; see
 *   `Event.ts`.
 */
export const SETTLEMENT_FIELDS = gql`
  fragment SettlementFields on Settlement {
    id
    pdfDownloadUrl
    settlementType
    businessName
    taxId
    taxWithholdingAmount
    taxWithholdingType
    taxLabel
    eventIds
    address {
      ...SettlementAddressFields
    }
    availableEvents {
      ...SettlementEventFields
    }
    events {
      ...SettlementEventFields
    }
    barDeals {
      ...SettlementDealFields
    }
    ticketDeals {
      ...SettlementDealFields
    }
    customExpenses {
      ...SettlementCustomExpenseFields
    }
    manualTicketSales {
      ...SettlementManualTicketSaleFields
    }
    payments {
      ...SettlementPaymentFields
    }
  }
  ${SETTLEMENT_ATTACHMENT_FIELDS}
  ${SETTLEMENT_ADDRESS_FIELDS}
  ${SETTLEMENT_EVENT_FIELDS}
  ${SETTLEMENT_DEAL_FIELDS}
  ${SETTLEMENT_CUSTOM_EXPENSE_FIELDS}
  ${SETTLEMENT_MANUAL_TICKET_SALE_FIELDS}
  ${SETTLEMENT_PAYMENT_FIELDS}
`;
